import React, { useState } from "react";
import { Mail, ArrowRight, Instagram, Twitter, Linkedin, Check } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { useReveal } from "../../hooks/useReveal";

export const Newsletter = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [ref, inView] = useReveal();

  const submit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    api.addSubscriber(email).catch(() => {});
    setDone(true);
    setEmail("");
    setTimeout(() => setDone(false), 4000);
  };

  return (
    <section className="py-16 sm:py-24">
      <div ref={ref} className={`reveal ${inView ? "in-view" : ""} max-w-5xl mx-auto px-5 sm:px-8`}>
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-indigo-600 to-violet-600 p-8 sm:p-14 text-center">
          <div className="absolute -top-16 -end-10 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -start-10 w-72 h-72 rounded-full bg-black/10 blur-2xl" />
          <div className="relative">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-white/15 items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display font-extrabold text-2xl sm:text-4xl text-white tracking-tight max-w-2xl mx-auto text-balance">
              {t("sections.newsletter")}
            </h2>
            <form onSubmit={submit} className="mt-8 max-w-md mx-auto">
              <div className="flex items-center gap-2 bg-white rounded-full p-1.5 shadow-xl">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("sections.newsletterPlaceholder")}
                  className="flex-1 bg-transparent outline-none px-4 py-2.5 text-neutral-800 placeholder:text-neutral-400"
                />
                <Button type="submit" className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-6 h-11 shrink-0">
                  {done ? <Check className="w-5 h-5" /> : t("sections.newsletterCta")}
                </Button>
              </div>
              {done && <p className="mt-3 text-white/90 text-sm font-medium">{t("sections.newsletterSuccess")}</p>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const cols = [
    { title: "company", links: ["about", "careers", "press"] },
    { title: "support", links: ["help", "contact", "feedback"] },
    { title: "forExperts", links: ["becomeExpert", "expertLogin"] },
    { title: "legal", links: ["privacy", "terms"] },
  ];
  return (
    <footer className="bg-neutral-950 text-neutral-400 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center">
                <span className="text-neutral-900 font-display font-extrabold text-lg">i</span>
              </div>
              <span className="font-display font-extrabold text-xl text-white">intro</span>
            </div>
            <p className="text-sm max-w-xs leading-relaxed">{t("footer.tagline")}</p>
            <div className="flex items-center gap-3 mt-5">
              {[Instagram, Twitter, Linkedin].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors">
                  <Icon className="w-4 h-4 text-white" />
                </a>
              ))}
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="text-white font-semibold text-sm mb-4">{t(`footer.${col.title}`)}</h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="#" className="text-sm hover:text-white transition-colors">{t(`footer.${l}`)}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p>© {new Date().getFullYear()} intro. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};
